import { useMemo, useState, useCallback } from "react";
import { useQuery, useAction } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { RegressionDetail } from "./RegressionDetail";

// --- Helpers ---

function formatElapsed(startedAt: number): string {
  const elapsed = Date.now() - startedAt;
  const totalSeconds = Math.floor(elapsed / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return `${h}h ${remM}m`;
}

function formatStartTime(ts: number): string {
  return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** Returns true when an execution has been running for more than 1 hour. */
function isStale(startedAt: number): boolean {
  return Date.now() - startedAt > 3_600_000;
}

// --- Per-project row: subscribes to executions for one PRD Execution ---

function ProjectExecutionRow({
  project,
  repoName,
}: {
  project: { _id: Id<"projects">; name: string; repositoryId?: Id<"repositories"> };
  repoName: string;
}) {
  // Fetch all executions for this PRD Execution — reactive subscription
  const executions = useQuery(api.executions.list, { projectId: project._id });

  const running = useMemo(
    () => (executions ?? []).filter((e) => e.status === "running"),
    [executions],
  );

  if (running.length === 0) return null;

  return (
    <>
      {running.map((exec) => {
        const stale = isStale(exec.startedAt);
        const correlationId =
          exec.metadata?.correlationId ??
          (typeof exec.metadata === "object" && exec.metadata !== null
            ? (exec.metadata as Record<string, unknown>).correlationId
            : undefined);
        const correlationShort =
          typeof correlationId === "string" ? correlationId.slice(0, 8) : null;
        const modelConfig =
          exec.metadata?.modelConfig ??
          (typeof exec.metadata === "object" && exec.metadata !== null
            ? (exec.metadata as Record<string, unknown>).modelConfig
            : undefined);
        const workflowStatus =
          typeof exec.metadata === "object" && exec.metadata !== null
            ? (exec.metadata as Record<string, unknown>).workflowStatus
            : undefined;

        return (
          <tr
            key={exec._id}
            className={`border-b border-zinc-800/50 ${stale ? "bg-amber-500/5" : ""}`}
          >
            {/* PRD Execution name */}
            <td className="px-4 py-3 text-sm text-zinc-100 font-medium whitespace-nowrap">
              {project.name}
            </td>
            {/* Project (repo) */}
            <td className="px-4 py-3 text-sm text-zinc-400 whitespace-nowrap">{repoName}</td>
            {/* Task ID */}
            <td className="px-4 py-3 text-sm font-mono text-zinc-300 whitespace-nowrap">
              {exec.taskId}
            </td>
            {/* Start time */}
            <td className="px-4 py-3 text-sm text-zinc-400 whitespace-nowrap">
              {formatStartTime(exec.startedAt)}
            </td>
            {/* Elapsed */}
            <td className="px-4 py-3 text-sm font-mono text-zinc-300 whitespace-nowrap">
              {formatElapsed(exec.startedAt)}
              {stale && (
                <span
                  className="ml-2 inline-flex items-center gap-1 text-amber-400 text-xs font-sans"
                  title="Running for more than 1 hour"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                    />
                  </svg>
                  stale
                </span>
              )}
            </td>
            {/* Correlation ID */}
            <td className="px-4 py-3 text-sm whitespace-nowrap">
              {correlationShort ? (
                <span
                  className="font-mono text-zinc-500 cursor-default"
                  title={typeof correlationId === "string" ? correlationId : undefined}
                >
                  {correlationShort}
                </span>
              ) : (
                <span className="text-zinc-700">-</span>
              )}
            </td>
            {/* Workflow status */}
            <td className="px-4 py-3 text-sm text-zinc-400 whitespace-nowrap">
              {typeof workflowStatus === "string" ? workflowStatus : "-"}
            </td>
            {/* Model config badge */}
            <td className="px-4 py-3 text-sm whitespace-nowrap">
              {typeof modelConfig === "string" ? (
                <span className="inline-flex items-center rounded-md bg-indigo-500/10 border border-indigo-500/20 px-2 py-0.5 text-xs font-medium text-indigo-300">
                  {modelConfig}
                </span>
              ) : (
                <span className="text-zinc-700">-</span>
              )}
            </td>
          </tr>
        );
      })}
    </>
  );
}

// --- Regression alert banner (FR-B04) ---

function RegressionBanner({
  projectId,
  projectName,
  onViewDetails,
}: {
  projectId: Id<"projects">;
  projectName: string;
  onViewDetails: (projectId: Id<"projects">) => void;
}) {
  const alerts = useQuery(api.regression.activeAlerts, { projectId });
  if (!alerts || alerts.length === 0) return null;

  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <svg
          className="w-5 h-5 text-amber-400 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
          />
        </svg>
        <div>
          <span className="text-sm font-medium text-amber-300">
            {alerts.length} regression alert{alerts.length !== 1 ? "s" : ""} detected
          </span>
          <span className="text-xs text-zinc-500 ml-2">
            in {projectName}
          </span>
        </div>
      </div>
      <button
        onClick={() => onViewDetails(projectId)}
        className="text-xs text-amber-400 hover:text-amber-300 underline underline-offset-2"
      >
        View details
      </button>
    </div>
  );
}

// --- Wave Progress Section (FR-B03) ---

function WaveProgressSection({
  projectId,
  projectName,
}: {
  projectId: Id<"projects">;
  projectName: string;
}) {
  const waves = useQuery(api.tasks.waves, { projectId });
  const waveStatuses = useQuery(api.waveDispatch.waveDispatchStatus, {
    projectId,
  });

  if (!waves || waves.length === 0) return null;

  // Build a status lookup from the wave dispatch meta entries
  const statusMap = useMemo(() => {
    const map = new Map<string, { status: string; updatedAt: number }>();
    for (const ws of waveStatuses ?? []) {
      map.set(ws.waveLabel, { status: ws.status, updatedAt: ws.updatedAt });
    }
    return map;
  }, [waveStatuses]);

  const waveStatusColors: Record<string, string> = {
    dispatching: "text-blue-400",
    wave_complete: "text-green-400",
    wave_paused: "text-amber-400",
    project_complete: "text-green-400",
  };

  const taskStatusColors: Record<string, string> = {
    pending: "bg-zinc-700",
    setup: "bg-zinc-600",
    waiting: "bg-zinc-500",
    in_progress: "bg-blue-500",
    complete: "bg-green-500",
    failed: "bg-red-500",
    needs_human: "bg-amber-500",
    skipped: "bg-zinc-600",
    deferred: "bg-zinc-500",
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-100">
          Wave Progress — {projectName}
        </h3>
        <span className="text-xs text-zinc-500">
          {waves.length} wave{waves.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="space-y-3">
        {waves.map((wave: any) => {
          const waveStatus = statusMap.get(wave.label);
          const completedCount = wave.done ?? 0;
          const totalCount = wave.total ?? 0;
          const progress =
            totalCount > 0
              ? Math.round((completedCount / totalCount) * 100)
              : 0;

          return (
            <div
              key={wave.label}
              className="border border-zinc-800/60 rounded-lg p-3 space-y-2"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono font-medium text-zinc-300">
                    {wave.label}
                  </span>
                  {wave.parallel && (
                    <span className="text-[10px] text-zinc-600 border border-zinc-700 rounded px-1">
                      parallel
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {waveStatus && (
                    <span
                      className={`text-[10px] font-medium ${waveStatusColors[waveStatus.status] ?? "text-zinc-500"}`}
                    >
                      {waveStatus.status.replace("_", " ")}
                    </span>
                  )}
                  <span className="text-xs text-zinc-500">
                    {completedCount}/{totalCount}
                  </span>
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-zinc-800 rounded-full h-1.5">
                <div
                  className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>

              {/* Task status cards */}
              <div className="flex flex-wrap gap-1.5">
                {(wave.tasks ?? []).map((task: any) => (
                  <div
                    key={task.taskId}
                    className="flex items-center gap-1.5 bg-zinc-800/50 rounded px-2 py-1"
                    title={`${task.taskId}: ${task.subject} (${task.status})`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${taskStatusColors[task.status] ?? "bg-zinc-600"}`}
                    />
                    <span className="text-[10px] font-mono text-zinc-400">
                      {task.taskId}
                    </span>
                    {task.ready && task.status === "pending" && (
                      <span className="text-[10px] text-green-500">ready</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Main Fleet View page ---

export function FleetView() {
  // All PRD Executions (projects table) — reactive
  const allProjects = useQuery(api.projects.list, {});
  // All Projects (repositories table) — reactive
  const repositories = useQuery(api.repositories.list, {});

  // Filter to non-archived PRD Executions
  const activeProjects = useMemo(
    () => (allProjects ?? []).filter((p) => !p.archived),
    [allProjects],
  );

  // Build repo lookup
  const repoMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of repositories ?? []) {
      map.set(r._id, r.name);
    }
    return map;
  }, [repositories]);

  // Regression detail drill-down state
  const [regressionDetailId, setRegressionDetailId] = useState<Id<"projects"> | null>(null);

  const loading = allProjects === undefined || repositories === undefined;

  // If showing regression detail, render that page instead
  if (regressionDetailId) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <button
          onClick={() => setRegressionDetailId(null)}
          className="text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back to Fleet View
        </button>
        <RegressionDetail projectId={regressionDetailId} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-zinc-100">Fleet View</h2>
        <p className="text-sm text-zinc-500 mt-1">
          Active agent sessions across all PRD Executions. Real-time status via reactive
          subscriptions.
        </p>
      </div>

      {/* Regression banners for each active PRD Execution (FR-B04) */}
      {!loading && activeProjects.length > 0 && (
        <div className="space-y-2">
          {activeProjects.map((project) => (
            <RegressionBanner
              key={`reg-${project._id}`}
              projectId={project._id}
              projectName={project.name}
              onViewDetails={setRegressionDetailId}
            />
          ))}
        </div>
      )}

      {/* Wave progress section (FR-B03) */}
      {!loading &&
        activeProjects.map((project) => (
          <WaveProgressSection
            key={`wave-${project._id}`}
            projectId={project._id}
            projectName={project.name}
          />
        ))}

      {loading ? (
        <div className="text-sm text-zinc-600">Loading...</div>
      ) : activeProjects.length === 0 ? (
        <div className="text-center py-16 text-zinc-600">
          <p className="text-sm">No active PRD Executions.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-zinc-800 bg-zinc-900/60">
                <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  PRD Execution
                </th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Project
                </th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Task
                </th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Started
                </th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Elapsed
                </th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Correlation
                </th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Workflow
                </th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Model
                </th>
              </tr>
            </thead>
            <tbody>
              {activeProjects.map((project) => (
                <ProjectExecutionRow
                  key={project._id}
                  project={project}
                  repoName={
                    project.repositoryId
                      ? repoMap.get(project.repositoryId) ?? "unknown"
                      : "-"
                  }
                />
              ))}
            </tbody>
          </table>

          {/* Empty-state row rendered if table body has no running rows.
              We use a summary line below the table instead. */}
        </div>
      )}

      <p className="text-xs text-zinc-600">
        Rows appear only for executions with status &quot;running&quot;. Stale sessions
        (&gt; 1 hour) are highlighted with a yellow warning.
      </p>
    </div>
  );
}
